import React from "react";
import {
    Home,
    Building,
    Building2,
    Trees,
    Layers,
    Wrench,
    FileText,
    SearchCheck,
    Handshake,
    KeyRound,
} from "lucide-react";

import { PageShell } from "../components/layout/PageShell";
import { Container } from "../components/zen/Container";
import { Section } from "../components/zen/Section";
import { Eyebrow } from "../components/zen/Eyebrow";
import { Heading } from "../components/zen/Heading";
import { Button } from "../components/zen/Button";
import { PageHero } from "../components/zen/PageHero";
import { CapabilityCard } from "../components/zen/CapabilityCard";
import { Timeline } from "../components/zen/Timeline";
import { ClosingCTA } from "../components/zen/ClosingCTA";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormSuccess,
    FormCard,
    useLeadSubmit,
} from "../components/zen/FormField";
import { FormSubmit } from "../components/zen/FormSubmit";

const ASSETS = [
    {
        icon: Home,
        title: "Single-Family Residential",
        description: "Owner-occupied and tenanted homes across target markets.",
    },
    {
        icon: Building,
        title: "Small Multi-Family",
        description: "Two to twenty-unit buildings with stable cash flow.",
    },
    {
        icon: Building2,
        title: "Commercial / Mixed-Use",
        description: "Retail, office, and mixed-use assets with operator fit.",
    },
    {
        icon: Trees,
        title: "Land & Development Sites",
        description: "Entitled and pre-development parcels for future projects.",
    },
    {
        icon: Layers,
        title: "Off-Market Portfolios",
        description: "Multi-asset portfolios sourced through direct relationships.",
    },
    {
        icon: Wrench,
        title: "Distressed or Underutilized",
        description: "Repositioning opportunities backed by long-term operator focus.",
    },
];

const STEPS = [
    {
        number: "01",
        title: "Submit Property",
        description: "Share property details through the form below.",
        icon: FileText,
    },
    {
        number: "02",
        title: "Initial Review",
        description:
            "Our team reviews fundamentals and confirms fit within 48 hours.",
        icon: SearchCheck,
    },
    {
        number: "03",
        title: "Direct Offer",
        description:
            "Receive a clear, written offer — no obligation, no intermediaries.",
        icon: Handshake,
    },
    {
        number: "04",
        title: "Closing",
        description:
            "Flexible closing on your timeline, including all-cash and creative structures.",
        icon: KeyRound,
    },
];

const FORM_FIELDS = [
    { name: "owner_name", required: true, type: "text" },
    { name: "email", required: true, type: "email" },
    { name: "phone", required: true, type: "text" },
    { name: "property_address", required: true, type: "text" },
    { name: "property_type", required: true, type: "select" },
    { name: "asking_price", required: false, type: "text" },
    { name: "property_condition", required: true, type: "select" },
    { name: "reason_for_selling", required: false, type: "text" },
];

export default function RealEstate() {
    const form = useLeadSubmit({
        endpoint: "/leads/realestate",
        fields: FORM_FIELDS,
    });

    return (
        <PageShell>
            <div data-testid="page-real-estate">
                <PageHero
                    eyebrow="Real Estate"
                    title="Off-market acquisitions and direct property solutions."
                    subtitle="Direct partnership for sellers seeking a quiet, efficient transaction. We acquire residential and small commercial assets across the region."
                    data-testid="re-hero"
                >
                    <Button
                        href="#submit"
                        variant="primary"
                        size="lg"
                        data-testid="re-cta-submit"
                    >
                        Submit a Property
                    </Button>
                    <Button
                        to="/contact"
                        variant="ghost"
                        size="lg"
                        data-testid="re-cta-contact"
                    >
                        Talk to Our Team
                    </Button>
                </PageHero>

                {/* WHAT WE ACQUIRE */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>What We Acquire</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                A focused acquisition mandate.
                            </Heading>
                        </div>
                        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                            {ASSETS.map((a, i) => (
                                <CapabilityCard
                                    key={a.title}
                                    {...a}
                                    data-testid={`re-asset-${i}`}
                                />
                            ))}
                        </div>
                    </Container>
                </Section>

                {/* PROCESS */}
                <Section className="border-t border-border-subtle">
                    <Container>
                        <div className="max-w-2xl">
                            <Eyebrow>Process</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                A direct, four-step acquisition path.
                            </Heading>
                        </div>
                        <Timeline steps={STEPS} data-testid="re-timeline" />
                    </Container>
                </Section>

                {/* FORM */}
                <Section
                    id="submit"
                    className="scroll-mt-24 border-y border-border-subtle bg-elevated"
                >
                    <Container>
                        <div className="mx-auto max-w-2xl text-center">
                            <Eyebrow>Submit a Property</Eyebrow>
                            <Heading as="h2" className="mt-4">
                                Tell us about the property.
                            </Heading>
                            <p className="mt-5 text-[16px] leading-[1.65] text-text-secondary">
                                Share the details below — we respond with an initial review within 48 hours.
                            </p>
                        </div>
                        <div className="mx-auto mt-12 max-w-2xl">
                            <FormCard data-testid="re-form-card">
                                {form.success ? (
                                    <FormSuccess
                                        title="Submission received."
                                        message="Thank you. Our acquisitions team will review and respond within 48 hours."
                                        data-testid="re-form-success"
                                    />
                                ) : (
                                    <form
                                        onSubmit={form.onSubmit}
                                        noValidate
                                        className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                                        data-testid="re-form"
                                    >
                                        <FormField
                                            label="Owner Name"
                                            name="owner_name"
                                            required
                                            value={form.values.owner_name}
                                            onChange={form.onChange}
                                            error={form.errors.owner_name}
                                            data-testid="re-input-owner"
                                        />
                                        <FormField
                                            label="Email"
                                            name="email"
                                            type="email"
                                            required
                                            value={form.values.email}
                                            onChange={form.onChange}
                                            error={form.errors.email}
                                            data-testid="re-input-email"
                                        />
                                        <FormField
                                            label="Phone"
                                            name="phone"
                                            type="tel"
                                            required
                                            value={form.values.phone}
                                            onChange={form.onChange}
                                            error={form.errors.phone}
                                            data-testid="re-input-phone"
                                        />
                                        <FormField
                                            label="Asking Price (optional)"
                                            name="asking_price"
                                            placeholder="e.g., $450,000 or open"
                                            value={form.values.asking_price}
                                            onChange={form.onChange}
                                            data-testid="re-input-price"
                                        />
                                        <div className="sm:col-span-2">
                                            <FormField
                                                label="Property Address"
                                                name="property_address"
                                                required
                                                placeholder="Street, City, State, ZIP"
                                                value={form.values.property_address}
                                                onChange={form.onChange}
                                                error={form.errors.property_address}
                                                data-testid="re-input-address"
                                            />
                                        </div>
                                        <FormSelect
                                            label="Property Type"
                                            name="property_type"
                                            required
                                            value={form.values.property_type}
                                            onChange={form.onChange}
                                            error={form.errors.property_type}
                                            options={[
                                                "Single-Family",
                                                "Multi-Family",
                                                "Commercial",
                                                "Land",
                                                "Other",
                                            ]}
                                            data-testid="re-input-type"
                                        />
                                        <FormSelect
                                            label="Property Condition"
                                            name="property_condition"
                                            required
                                            value={form.values.property_condition}
                                            onChange={form.onChange}
                                            error={form.errors.property_condition}
                                            options={[
                                                "Excellent",
                                                "Good",
                                                "Fair",
                                                "Needs Work",
                                                "Distressed",
                                            ]}
                                            data-testid="re-input-condition"
                                        />
                                        <div className="sm:col-span-2">
                                            <FormTextarea
                                                label="Reason for Selling"
                                                name="reason_for_selling"
                                                placeholder="Optional context — timeline, situation, etc."
                                                value={form.values.reason_for_selling}
                                                onChange={form.onChange}
                                                data-testid="re-input-reason"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormSubmit
                                                label="Submit Property"
                                                submitting={form.submitting}
                                                data-testid="re-submit-btn"
                                            />
                                        </div>
                                    </form>
                                )}
                            </FormCard>
                        </div>
                    </Container>
                </Section>

                <ClosingCTA
                    eyebrow="Direct Partnership"
                    title="A quiet, efficient transaction."
                    subtitle="Whether you're considering a single property or a portfolio — we work directly with sellers, no intermediaries."
                    primary={{ label: "Contact Us", to: "/contact" }}
                    secondary={{ label: "Explore Divisions", to: "/divisions" }}
                />
            </div>
        </PageShell>
    );
}
